import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Alert, Modal } from 'react-bootstrap';
import Container from '../components/Container';
import Button from '../components/Button';
import { useAuth } from '../context/AuthContext';
import { useActualites } from '../context/ActualitesContext';
import ActualiteForm from '../components/ActualiteForm';
import { Plus, Edit, Trash2, LogOut, FileText, Users } from 'lucide-react';
import '../styles/components.css';

const Admin = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { actualites, deleteActualite, loading } = useActualites();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingActualite, setEditingActualite] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actualiteToDelete, setActualiteToDelete] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleAdd = () => {
    setEditingActualite(null);
    setShowForm(true);
  };

  const handleEdit = (actualite) => {
    setEditingActualite(actualite);
    setShowForm(true);
  };

  const handleDelete = (actualite) => {
    setActualiteToDelete(actualite);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (actualiteToDelete) {
      deleteActualite(actualiteToDelete.id);
      setShowDeleteModal(false);
      setActualiteToDelete(null);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingActualite(null);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="section">
      <Container>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 'var(--spacing-md)'
            }}>
              <div>
                <h1 style={{ 
                  fontSize: 'var(--font-size-4xl)', 
                  fontWeight: 'var(--font-weight-bold)',
                  marginBottom: 'var(--spacing-sm)',
                  color: 'var(--color-text)'
                }}>
                  Administration
                </h1>
                <p style={{ 
                  color: 'var(--color-text-light)',
                  margin: 0
                }}>
                  Bienvenue, {user?.name || user?.email}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
                <Button
                  variant="outline"
                  onClick={() => navigate('/actualite')}
                >
                  <FileText size={18} style={{ marginRight: '0.5rem' }} />
                  Voir les actualités
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/candidatures')}
                >
                  <Users size={18} style={{ marginRight: '0.5rem' }} />
                  Voir les candidatures
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                >
                  <LogOut size={18} style={{ marginRight: '0.5rem' }} />
                  Déconnexion
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Section Actualités */}
        <div className="card-modern" style={{ marginBottom: 'var(--spacing-2xl)' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 'var(--spacing-xl)',
            flexWrap: 'wrap',
            gap: 'var(--spacing-md)'
          }}>
            <div>
              <h2 style={{ 
                fontSize: 'var(--font-size-2xl)', 
                fontWeight: 'var(--font-weight-semibold)',
                marginBottom: 'var(--spacing-sm)',
                color: 'var(--color-text)'
              }}>
                Gestion des actualités
              </h2>
              <p style={{ 
                color: 'var(--color-text-light)',
                margin: 0
              }}>
                Ajoutez, modifiez ou supprimez des actualités
              </p>
            </div>
            <Button
              variant="primary"
              onClick={handleAdd}
            >
              <Plus size={20} style={{ marginRight: '0.5rem' }} />
              Ajouter une actualité
            </Button>
          </div>

          {loading ? (
            <p style={{ textAlign: 'center', color: 'var(--color-text-light)' }}>
              Chargement...
            </p>
          ) : actualites.length === 0 ? (
            <Alert variant="info" style={{ textAlign: 'center' }}>
              Aucune actualité pour le moment. Cliquez sur "Ajouter une actualité" pour commencer.
            </Alert>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--spacing-lg)' }}>
              {actualites.map((actualite) => (
                <div 
                  key={actualite.id}
                  style={{
                    padding: 'var(--spacing-lg)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--border-radius-lg)',
                    backgroundColor: 'var(--color-bg)',
                    transition: 'var(--transition-base)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Row className="align-items-center">
                    <Col md={8}>
                      <h3 style={{ 
                        fontSize: 'var(--font-size-xl)', 
                        fontWeight: 'var(--font-weight-semibold)',
                        marginBottom: 'var(--spacing-sm)',
                        color: 'var(--color-text)'
                      }}>
                        {actualite.title}
                      </h3>
                      <p style={{ 
                        color: 'var(--color-text-light)',
                        fontSize: 'var(--font-size-sm)',
                        marginBottom: 'var(--spacing-xs)'
                      }}>
                        {actualite.date} {actualite.location && `• ${actualite.location}`}
                      </p>
                      <p style={{ 
                        color: 'var(--color-text-light)',
                        fontSize: 'var(--font-size-base)',
                        margin: 0,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {actualite.description}
                      </p>
                    </Col>
                    <Col md={4} className="text-end">
                      <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(actualite)}
                        >
                          <Edit size={16} style={{ marginRight: '0.25rem' }} />
                          Modifier
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(actualite)}
                          style={{ color: 'var(--color-error)' }}
                        >
                          <Trash2 size={16} style={{ marginRight: '0.25rem' }} />
                          Supprimer
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          )}
        </div>
      </Container>

      {/* Modal Formulaire */}
      <Modal 
        show={showForm} 
        onHide={handleFormClose}
        size="lg"
        centered
      >
        <Modal.Header closeButton style={{ borderBottom: '1px solid var(--color-border)' }}>
          <Modal.Title>
            {editingActualite ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ActualiteForm
            actualite={editingActualite}
            onClose={handleFormClose}
          />
        </Modal.Body>
      </Modal>

      {/* Modal Confirmation Suppression */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Êtes-vous sûr de vouloir supprimer l'actualité 
            <strong> "{actualiteToDelete?.title}"</strong> ?
            <br />
            Cette action est irréversible.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={confirmDelete}
            style={{ backgroundColor: 'var(--color-error)', borderColor: 'var(--color-error)' }}
          >
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Admin;
